import clsx from "clsx";
import { FC, useEffect, useMemo } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { Msg, MsgExec, priceFormatter } from "rujira.js";
import {
  Button,
  Decimal,
  /* Decimal, */
  i18n,
  Numeric,
  useGlobalModalContext,
} from "rujira.ui";
import exclamation from "rujira.ui/src/assets/images/exclamation.gif";
import { Info } from "rujira.ui/src/components/icons/Icons";
import { usePreloadedBalance } from "../../common/components/Balance";
import { useNotificationModal } from "../../common/components/Notifications";
import {
  MsgProvider,
  TxBalanceAccountButton,
  TxButton,
} from "../../common/components/TxButton";
import {} from "../../portfolio/utils";
import { Side } from "../types";
import {
  SubmitLimitFragment$data,
  SubmitLimitFragment$key,
} from "./__generated__/SubmitLimitFragment.graphql";
import { AutoClaimerToggle } from "./AutoClaimerToggle";
import { adjust, useAmount, usePrice } from "./Context";
import { InputQuantity, toDecimal } from "./InputQuantity";

const fragment = graphql`
  fragment SubmitLimitFragment on FinPair {
    address
    tick
    assetBase {
      asset
      type
      chain
      metadata {
        decimals
        symbol
      }
    }
    assetQuote {
      asset
      type
      chain
      metadata {
        decimals
        symbol
      }
    }
    oracleBase {
      id
      price
    }
    oracleQuote {
      id
      price
    }
    book {
      bids {
        price
      }
      center
      asks {
        price
      }
    }
    feeMaker
    feeTaker
  }
`;

interface Props {
  k: SubmitLimitFragment$key;
  side: Side;
}

export const SubmitLimit: FC<Props> = ({ k, side }) => {
  const [amount, setAmount] = useAmount();

  const d = useFragment(fragment, k);

  const [price, setPrice] = usePrice();
  useEffect(() => {
    setPrice(adjust(BigInt(d.book.center || 0), Number(d.tick)));
  }, []);

  const { request } = useNotificationModal();
  const { account } = usePreloadedBalance();
  const msg = useMemo(() => {
    if (!amount) return null;
    if (!account) return null;
    const orders = [[side, { fixed: toDecimal(price) }, amount.toString()]];
    return d
      ? new MsgExec(
          { address: account.address, network: "THOR" },
          account.asset,
          amount,
          d.address,
          { order: [orders, null] }
        )
      : null;
  }, [side, price, amount, d.address]);

  const ask = d.book.asks[0] && BigInt(d.book.asks[0].price);
  const bid = d.book.bids[0] && BigInt(d.book.bids[0].price);
  const mid = d.book.center && BigInt(d.book.center);
  const deviation = mid ? ((price - mid) * 10000n) / mid : 0n;

  const priceAlert =
    (side === Side.Quote && ask && price > (ask * 10010n) / 10000n) ||
    (side === Side.Base && bid && price < (bid * 9990n) / 10000n);

  const buttonLabel = `${side === Side.Quote ? "Buy" : "Sell"} ${d.assetBase ? d.assetBase.metadata.symbol : ""}`;

  const { showModal, hideModal } = useGlobalModalContext();

  const confirm = (msg: Msg, deviation: bigint) => {
    showModal({
      //title: "Confirm",
      backgroundClose: true,
      children: (
        <MsgProvider msg={msg}>
          <ConfirmModal
            deviation={deviation}
            side={side}
            label={buttonLabel}
            onSuccess={request}
            cancel={hideModal}
          />
        </MsgProvider>
      ),
    });
  };

  return (
    <MsgProvider msg={msg}>
      <InputQuantity amount={amount} setAmount={setAmount}>
        <i18n.label>
          Qty.{" "}
          <small>
            (
            {side === Side.Quote
              ? d.assetQuote.metadata.symbol
              : d.assetBase.metadata.symbol}
            )
          </small>
        </i18n.label>
      </InputQuantity>
      <div>
        <Numeric
          className={clsx({
            "numeric-input numeric-input--white condensed": true,
            "numeric-input--error": priceAlert,
          })}
          amount={price}
          onChangeAmount={setPrice}
          decimals={12}
          // onValueChange={(x) => setPrice(x.value)}
        >
          <i18n.label>
            Price <small>({d.assetQuote.metadata.symbol})</small>
          </i18n.label>
        </Numeric>

        <div className="flex ai-c jc-c mx-a mt-0.5">
          {side === Side.Base ? (
            <>
              <i18n.button
                className="trade__submit-sm-btn"
                onClick={() => bid && setPrice(adjust(bid, Number(d.tick)))}>
                Bid
              </i18n.button>

              <i18n.button
                className="trade__submit-sm-btn ml-0.5"
                onClick={() => mid && setPrice(adjust(mid, Number(d.tick)))}>
                Mid
              </i18n.button>
              <i18n.button
                className="trade__submit-sm-btn ml-0.5"
                onClick={() => ask && setPrice(adjust(ask, Number(d.tick)))}>
                Ask
              </i18n.button>

              <i18n.button
                className="trade__submit-sm-btn ml-0.5"
                onClick={() =>
                  price &&
                  d &&
                  setPrice(adjust((price * 102n) / 100n, Number(d.tick)))
                }>
                +2%
              </i18n.button>
            </>
          ) : (
            <>
              <i18n.button
                className="trade__submit-sm-btn"
                onClick={() => ask && setPrice(adjust(ask, Number(d.tick)))}>
                Ask
              </i18n.button>

              <i18n.button
                className="trade__submit-sm-btn ml-0.5"
                onClick={() => mid && setPrice(adjust(mid, Number(d.tick)))}>
                Mid
              </i18n.button>
              <i18n.button
                className="trade__submit-sm-btn ml-0.5"
                onClick={() => bid && setPrice(adjust(bid, Number(d.tick)))}>
                Bid
              </i18n.button>
              <i18n.button
                className="trade__submit-sm-btn ml-0.5"
                onClick={() =>
                  price &&
                  d &&
                  setPrice(adjust((price * 98n) / 100n, Number(d.tick)))
                }>
                -2%
              </i18n.button>
            </>
          )}
        </div>
      </div>
      <AutoClaimerToggle>
        <Quote amount={amount} side={side} data={d || undefined} />
        <p
          data-tooltip-content={i18n.t(
            "Buy or sell a given quantity of token at a limit price or better."
          )}
          data-tooltip-id="global-tip"
          className="color-grey fs-12 text-center flex ai-c jc-c mt-a fw-500">
          {i18n.t("What are limit orders?")}
          <Info className="w-2 h-2 ml-0.5" />
        </p>

        <div>
          <TxBalanceAccountButton
            required={{
              asset: side === Side.Base ? d.assetBase : d.assetQuote,
              amount,
            }}
            Component={(props) =>
              priceAlert ? (
                <Button
                  className="button--red w-full"
                  label={buttonLabel}
                  onClick={() => msg && confirm(msg, deviation)}>
                  <img src={exclamation} alt="" className="filter-white big" />
                </Button>
              ) : (
                <TxButton {...props} />
              )
            }
            hideSimulation
            onSuccess={request}
            label={buttonLabel}
            className={clsx({
              "w-full": true,
              "button--blue": side == Side.Quote,
              "button--red": side == Side.Base,
            })}
          />
        </div>
      </AutoClaimerToggle>
    </MsgProvider>
  );
};

const ConfirmModal: FC<{
  side: Side;
  label: string;
  deviation: bigint;
  onSuccess: () => void;
  cancel: () => void;
}> = ({ side, label, onSuccess, cancel, deviation }) => {
  return (
    <>
      <div className="flex ai-c jc-c">
        <img
          src={exclamation}
          alt=""
          className="filter-orange block w-6 h-a no-shrink"
        />
        <span className="color-orange fs-14 fw-600 mx-1">
          The requested price is{" "}
          <span className="color-white">
            {(Math.abs(Number(deviation)) / 100).toLocaleDecimal(2)}%
          </span>{" "}
          {deviation > 0 ? "above" : "below"} current market price. Proceed with
          caution.
        </span>
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            onSuccess={onSuccess}
            label={label}
            className={clsx({
              "button ml-1": true,
              "button--blue": side == Side.Quote,
              "button--red": side == Side.Base,
            })}
          />
        </div>
      </div>
    </>
  );
};

const Quote: FC<{
  data?: SubmitLimitFragment$data;
  amount: bigint;
  side: Side;
}> = ({ data, amount, side }) => {
  const [price] = usePrice();
  const totalReturn = price
    ? side === Side.Base
      ? (amount * price) / 10n ** 12n
      : (amount * 10n ** 12n) / price
    : 0n;

  const fee = (BigInt(data?.feeMaker || "0") * totalReturn) / 10n ** 12n;
  const returned = totalReturn - fee;

  return (
    <div className="flex dir-c mt-1 mb-2 gap-y-1 fs-12 color-grey condensed">
      <div className="flex ai-c grow trade__quote trade__quote--return">
        <i18n.div className="condensed color-primary1 grow fs-14">
          Return
        </i18n.div>
        <Decimal
          amount={BigInt(returned || "")}
          className="ml-a color-white fs-14"
          symbol={
            side === Side.Quote
              ? data?.assetBase.metadata.symbol
              : data?.assetQuote.metadata.symbol
          }
          symbolClassName="color-grey condensed symbol inline-block text-left"
        />
      </div>

      <div
        className="flex"
        data-tooltip-id="global-tip"
        data-tooltip-float={true}
        data-tooltip-content={`${(Number(data?.feeMaker) / 1e10).toLocaleDecimal(3)}% maker fee`}>
        <div className="col">{i18n.t("Fee")}*</div>
        <div className="col text-right color-white">
          {priceFormatter(fee * 10000n)}
          <span className="color-grey condensed symbol inline-block text-left ml-0.5">
            {side === Side.Quote
              ? data?.assetBase.metadata.symbol
              : data?.assetQuote.metadata.symbol}
          </span>
        </div>
      </div>
    </div>
  );
};
